import { FCAdditionalProps } from "@datassential/platform-ui-lib";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import Title from "@components/Title";
import cn from "classnames";
import Tabs from "@components/Tabs";
import { createContext, FC, useContext, useEffect, useMemo, useState } from "react";
import { useCategory, useMetrics } from "@core/CommonDataContext";
import { SelectSingle } from "@components/Select";
import { CONSUMER_GROUPS, ConsumerGroup } from "@models/ConsumerGroups";
import RespondentTypes from "@pages/Profile/components/ConsumerGroups/RespondentTypes";
import Restaurants from "@pages/Profile/components/ConsumerGroups/Restaurants";
import CStores from "@pages/Profile/components/ConsumerGroups/CStores";
import Supermarkets from "@pages/Profile/components/ConsumerGroups/Supermarkets";
import Hotels from "@pages/Profile/components/ConsumerGroups/Hotels";
import Brands from "@pages/Profile/components/ConsumerGroups/Brands";
import Delivery from "@pages/Profile/components/ConsumerGroups/Delivery";
import Segments from "@pages/Profile/components/ConsumerGroups/Segments";
import { compareMetrics, Metric, PROFILE_CONSUMER_GROUPS_METRICS_ORDER } from "@models/Metric";
import { RawNorm, useProfileContext } from "@pages/Profile/ProfileContext";
import ContentHeader from "@components/ContentHeader";
import { useProfileConsumerGroups, ProfileConsumerGroupsResponse } from "@core/api/useProfileConsumerGroups";
import { ALL_FOOD_BEVERAGE_CATEGORY } from "@core/constants";
import { ProfileDataResponseItem } from "@core/api/useProfileHeader";

export enum View {
    Table,
    Chart,
}

const viewTabsData = [
    { id: View.Table, title: "Table" },
    { id: View.Chart, title: "Chart" },
];

type ContextType = {
    data: ProfileConsumerGroupsResponse;
    isLoading: boolean;
    selectedRawNorm: RawNorm;
    selectedView: View;
    selectedConsumerGroup: ConsumerGroup;
    selectedCategoryId: number | null | undefined;
    selectedMetric: Metric | null | undefined;
    setSelectedMetric: (metric: Metric | null | undefined) => void;
};

const DEFAULTS: ContextType = {
    data: {},
    isLoading: false,
    selectedRawNorm: RawNorm.Raw,
    selectedView: View.Table,
    selectedConsumerGroup: ConsumerGroup.Demographics,
    selectedCategoryId: null,
    selectedMetric: null,
    setSelectedMetric: () => null,
}

const Context = createContext<ContextType>(DEFAULTS);

export function useConsumerGroupsContext() {
    return useContext(Context);
}

type ConsumeGroupsProps = Omit<FCAdditionalProps, 'id'> & {
    id: ProfileDataResponseItem["id"];
}

const ConsumerGroups: FC<ConsumeGroupsProps> = ({
    id,
    className,
    testId = "profileConsumerGroups",
    ...props
}) => {
    const [selectedView, setSelectedView] = useState<ContextType['selectedView']>(DEFAULTS.selectedView);
    const [selectedMetric, setSelectedMetric] = useState<ContextType['selectedMetric']>(DEFAULTS.selectedMetric);
    const [selectedConsumerGroup, setSelectedConsumerGroup] = useState<ContextType['selectedConsumerGroup']>(DEFAULTS.selectedConsumerGroup);
    const [everSwitchedToChart, setEverSwitchedToChart] = useState<boolean>(false);

    useEffect(() => {
        if (selectedView === View.Chart && !everSwitchedToChart) setEverSwitchedToChart(true);
    }, [selectedView, everSwitchedToChart]);

    // Set default metric to LoveIt if user has switched to chart view and no metric is selected
    useEffect(() => {
        if (everSwitchedToChart) setSelectedMetric((selectedMetric) => selectedMetric === null ? Metric.LoveIt : selectedMetric);
    }, [everSwitchedToChart]);

    const {
        rawNorms: selectedRawNorm,
        categoryId: selectedCategoryId,
    } = useProfileContext();

    const {
        data: consumerGroupsData,
        isLoading: isConsumerGroupsLoading,
    } = useProfileConsumerGroups(id, selectedCategoryId);

    const category = useCategory(selectedCategoryId);
    const selectedCategoryTitle = useMemo(() => {
        if (selectedRawNorm === RawNorm.Raw) return;

        return (category ? category?.name : ALL_FOOD_BEVERAGE_CATEGORY)?.toUpperCase();
    }, [category, selectedRawNorm]);

    const rawMetrics = useMetrics();

    const metrics = useMemo(
        () => (rawMetrics ?? [])
            .filter((i) => PROFILE_CONSUMER_GROUPS_METRICS_ORDER.includes(i.id as Metric))
            .sort((a, b) => compareMetrics(PROFILE_CONSUMER_GROUPS_METRICS_ORDER, a.id, b.id)),
        [rawMetrics],
    );

    const Content = useMemo(
        () => {
            switch (selectedConsumerGroup) {
                case ConsumerGroup.Demographics:
                    return RespondentTypes;
                case ConsumerGroup.Restaurants:
                    return Restaurants;
                case ConsumerGroup.CStores:
                    return CStores;
                case ConsumerGroup.Supermarkets:
                    return Supermarkets;
                case ConsumerGroup.Hotels:
                    return Hotels;
                case ConsumerGroup.Brands:
                    return Brands;
                case ConsumerGroup.Segments:
                    return Segments;
                case ConsumerGroup.DeliveryService:
                    return Delivery;
                default:
                    return () => <>No data.</>
            }
        },
        [selectedConsumerGroup]
    );

    const contextValue = useMemo(
        () => ({
            data: consumerGroupsData,
            isLoading: isConsumerGroupsLoading,
            selectedRawNorm,
            selectedView,
            selectedConsumerGroup,
            selectedCategoryId,
            selectedMetric,
            setSelectedMetric,
        }),
        [
            consumerGroupsData,
            isConsumerGroupsLoading,
            selectedRawNorm,
            selectedView,
            selectedConsumerGroup,
            selectedCategoryId,
            selectedMetric,
            setSelectedMetric,
        ]
    );

    return (
        <div
            className={cn(
                "ProfileConsumerGroups",
                className,
                "appears-top",
                "del-6",
            )}
            data-testid={testId}
            {...props}
        >
            <ContentHeader className="ProfileCGHeader">
                <Title
                    className={cn(
                        "appears-right",
                        "del-2",
                    )}
                    subTitle={selectedCategoryTitle ? <span className="appears-top fast">versus {selectedCategoryTitle}</span> : null}
                    testId="textProfileConsumerGroupsTitle"
                >
                    CONSUMER GROUPS
                </Title>
                <div className="ProfileCGHeader__content">
                    {selectedView === View.Chart && (
                        <SelectSingle
                            label="SHOW:"
                            value={selectedMetric}
                            setValue={setSelectedMetric}
                            className="appears-left fast"
                            options={[
                                { id: null, title: "Base" },
                                ...metrics.map((o) => ({
                                    id: o.id as Metric,
                                    title: o.name,
                                })),
                            ]}
                        />
                    )}
                    <div 
                        className={cn(
                            "ProfileCGHeader__filters",
                            "appears-left",
                            "del-2",
                        )}
                    >
                        <Tabs
                            data={viewTabsData}
                            modifiers={["height-l"]}
                            activeTabId={selectedView}
                            onItemClick={setSelectedView}
                            testId="blockProfileCGView"
                        />
                    </div>
                </div>
            </ContentHeader>
            <ContentWidget
                className={cn(
                    "ProfileCGWidget",
                    className,
                    "appears-top",
                    "del-6",
                )}
                testId={testId}
                isLoading={isConsumerGroupsLoading}
                {...props}
            >
                <div className="ProfileConsumerGroups__filters">
                    <div className="ProfileConsumerGroups__filters-part">
                        <Tabs
                            data={CONSUMER_GROUPS}
                            modifiers={["height-m", "naked"]}
                            activeTabId={selectedConsumerGroup}
                            onItemClick={setSelectedConsumerGroup}
                            testId="blockProfileCGFilters"
                        />
                    </div>
                </div>
                <div className="ProfileConsumerGroups__content-table">
                    <Context.Provider value={contextValue}>
                        <Content/>
                    </Context.Provider>
                </div>
            </ContentWidget>
        </div>
    );
};

export default ConsumerGroups;